import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/* ─────────────────────────────────────────────────────────────
   DATA
───────────────────────────────────────────────────────────── */

const MEGA_COLUMNS = [
  {
    heading: 'BLINDS',
    icon: 'ri-layout-column-line',
    links: [
      { label: 'Horizontal Blind Parts', href: '/category/horizontal-blind-parts' },
      { label: 'Vertical Blind Parts', href: '/category/vertical-blind-parts' },
      { label: 'Mini Blind Parts', href: '/category/mini-blind-parts' },
    ],
  },
  {
    heading: 'SHADES',
    icon: 'ri-contrast-2-line',
    links: [
      { label: 'Roller Shades', href: '/category/chains-cords' },
      { label: 'Cellular Shades', href: '/category/chains-cords' },
      { label: 'Pleated Shades', href: '/category/chains-cords' },
    ],
  },
  {
    heading: 'DRAPERY HARDWARE',
    icon: 'ri-ancient-gate-line',
    links: [
      { label: 'Rods', href: '/category/drapery-hardware' },
      { label: 'Carriers & Slides', href: '/category/drapery-hardware' },
      { label: 'Hooks & Rings', href: '/category/drapery-hardware' },
    ],
  },
  {
    heading: 'REPAIR PARTS',
    icon: 'ri-tools-line',
    links: [
      { label: 'Cord', href: '/category/chains-cords' },
      { label: 'Chain', href: '/category/chains-cords' },
      { label: 'Cord Weights', href: '/category/chains-cords' },
      { label: 'Tension Pulleys', href: '/category/chains-cords' },
    ],
  },
  {
    heading: 'MOUNTING HARDWARE',
    icon: 'ri-hammer-line',
    links: [
      { label: 'Brackets', href: '/category/mounting-brackets' },
      { label: 'Clips', href: '/category/valance-clips' },
      { label: 'Hold Downs', href: '/category/mounting-brackets' },
    ],
  },
];

const POPULAR_STRIP = [
  { label: 'Vane Savers', href: '/category/vertical-blind-parts' },
  { label: 'Valance Clips', href: '/category/valance-clips' },
  { label: 'Mounting Brackets', href: '/category/mounting-brackets' },
  { label: 'Cord Cleats', href: '/category/chains-cords' },
  { label: 'Chain & Clips', href: '/category/chains-cords' },
];

const CATEGORY_LIST = [
  { label: 'Horizontal Blinds', href: '/category/horizontal-blind-parts', icon: 'ri-menu-line' },
  { label: 'Vertical Blinds', href: '/category/vertical-blind-parts', icon: 'ri-layout-column-line' },
  { label: 'Mini Blinds', href: '/category/mini-blind-parts', icon: 'ri-line-height' },
  { label: 'Cellular Shades', href: '/category/chains-cords', icon: 'ri-grid-line' },
  { label: 'Roller Shades', href: '/category/chains-cords', icon: 'ri-contrast-2-line' },
  { label: 'Drapery', href: '/category/drapery-hardware', icon: 'ri-ancient-gate-line' },
  { label: 'Hardware', href: '/category/mounting-brackets', icon: 'ri-tools-line' },
];

const POPULAR_ITEMS = [
  { label: 'Vane Savers', href: '/category/vertical-blind-parts', desc: 'Most popular repair part' },
  { label: 'Valance Clips', href: '/category/valance-clips', desc: 'Universal fit, pack of 4' },
  { label: 'Mounting Brackets', href: '/category/mounting-brackets', desc: 'Inside & outside mount' },
  { label: 'Cord Cleats', href: '/category/chains-cords', desc: 'Child safety compliant' },
  { label: 'Chain & Clips', href: '/category/chains-cords', desc: '4.5mm standard bead chain' },
];

type ActiveMenu = 'mega' | 'category' | 'popular' | null;

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────── */
export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [activeMenu, setActiveMenu] = useState<ActiveMenu>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 4);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveMenu(null);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (searchOpen) setTimeout(() => searchRef.current?.focus(), 60);
  }, [searchOpen]);

  const go = useCallback((href: string) => {
    setActiveMenu(null);
    setMobileOpen(false);
    navigate(href);
  }, [navigate]);

  const openMenu = useCallback((key: ActiveMenu) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setActiveMenu(key);
  }, []);

  const scheduleClose = useCallback(() => {
    closeTimerRef.current = setTimeout(() => setActiveMenu(null), 140);
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery('');
    setSearchOpen(false);
  };

  const toggleMobileSection = (key: string) =>
    setMobileExpanded((prev) => (prev === key ? null : key));

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-shadow duration-200 ${
        scrolled || mobileOpen ? 'shadow-[0_2px_20px_rgba(0,0,0,0.08)]' : ''
      }`}
    >
      {/* ══════════════════ TOP UTILITY BAR ══════════════════ */}
      <div className="bg-stone-900">
        <div className="max-w-screen-2xl mx-auto px-4 xl:px-10 h-8 flex items-center justify-between">
          <a
            href="tel:+18005551234"
            className="flex items-center gap-1.5 text-stone-400 hover:text-amber-400 text-[11px] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-line text-[10px]" />
            1-800-555-1234
          </a>
          <span className="text-stone-400 text-[11px] hidden md:block tracking-wide">
            <span className="text-amber-400 font-bold">FREE SHIPPING</span>
            {' '}on orders $49+ &nbsp;·&nbsp; Combined shipping discounts &nbsp;·&nbsp; 1,000+ parts in stock
          </span>
          <span className="text-stone-500 text-[10px] md:hidden">Free ship $49+ · 1,000+ Parts</span>
          <div className="flex items-center gap-4">
            <button onClick={() => go('/about')} className="text-stone-500 hover:text-stone-300 text-[11px] transition-colors cursor-pointer whitespace-nowrap hidden sm:block">About</button>
            <button onClick={() => go('/about')} className="text-stone-500 hover:text-stone-300 text-[11px] transition-colors cursor-pointer whitespace-nowrap hidden sm:block">Contact</button>
          </div>
        </div>
      </div>

      {/* ══════════════════ DESKTOP NAV (lg+) ══════════════════ */}
      <div className="hidden lg:block border-b border-stone-100">
        {/* Main nav row — relative so mega/dropdowns position from here */}
        <div className="relative">
          <div className="max-w-screen-2xl mx-auto px-4 xl:px-10">
            <div className="flex items-center h-[60px] gap-6">

              {/* ── Logo */}
              <button onClick={() => go('/')} className="flex items-center gap-2.5 cursor-pointer flex-shrink-0 group">
                <div className="w-9 h-9 flex items-center justify-center bg-amber-600 rounded-md group-hover:bg-amber-500 transition-colors flex-shrink-0">
                  <i className="ri-home-gear-line text-white text-base" />
                </div>
                <div className="leading-none">
                  <div className="text-stone-900 font-extrabold text-sm tracking-widest whitespace-nowrap">BLINDS USA INC</div>
                  <div className="text-amber-600 text-[9px] tracking-[0.15em] uppercase font-bold mt-0.5 whitespace-nowrap">Parts &amp; Hardware · Since 1997</div>
                </div>
              </button>

              {/* ── Center nav */}
              <nav className="flex flex-1 items-center justify-center">

                {/* SHOP PARTS — triggers mega menu */}
                <div
                  className="relative"
                  onMouseEnter={() => openMenu('mega')}
                  onMouseLeave={scheduleClose}
                >
                  <button
                    className={`flex items-center gap-1 px-4 py-2 text-[13px] font-semibold rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                      activeMenu === 'mega' ? 'text-amber-700 bg-amber-50' : 'text-stone-700 hover:text-stone-900 hover:bg-stone-50'
                    }`}
                  >
                    Shop Parts
                    <i className={`ri-arrow-down-s-line text-base transition-transform duration-150 ${activeMenu === 'mega' ? 'rotate-180' : ''}`} />
                  </button>

                  {/* ────── TRUE MEGA MENU ────── */}
                  <div
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                    className={`absolute top-[calc(100%+1px)] left-1/2 -translate-x-1/2 w-[900px] bg-white border border-stone-100 rounded-xl transition-all duration-200 overflow-hidden ${
                      activeMenu === 'mega'
                        ? 'opacity-100 visible translate-y-0'
                        : 'opacity-0 invisible -translate-y-2 pointer-events-none'
                    }`}
                    style={{ boxShadow: '0 24px 60px rgba(0,0,0,0.12)', zIndex: 9999 }}
                  >
                    {/* 5-column grid */}
                    <div className="grid grid-cols-5 gap-0 p-8 pb-6">
                      {MEGA_COLUMNS.map((col, i) => (
                        <div
                          key={col.heading}
                          className={`${i < MEGA_COLUMNS.length - 1 ? 'border-r border-stone-100 pr-6 mr-6' : ''}`}
                        >
                          {/* Column heading */}
                          <div className="flex items-center gap-2 mb-4">
                            <span className="w-6 h-6 flex items-center justify-center rounded-md bg-amber-50 flex-shrink-0">
                              <i className={`${col.icon} text-amber-600 text-sm`} />
                            </span>
                            <span className="text-[10px] font-extrabold tracking-[0.14em] text-stone-400 uppercase whitespace-nowrap">
                              {col.heading}
                            </span>
                          </div>
                          {/* Links */}
                          <ul className="space-y-2">
                            {col.links.map((link) => (
                              <li key={link.label}>
                                <button
                                  onClick={() => go(link.href)}
                                  className="flex items-center gap-1.5 text-[13px] text-stone-600 hover:text-amber-700 transition-colors cursor-pointer text-left w-full leading-snug group"
                                >
                                  <i className="ri-arrow-right-s-line text-stone-300 group-hover:text-amber-400 text-sm flex-shrink-0 transition-colors" />
                                  {link.label}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>

                    {/* Popular strip */}
                    <div className="mx-8 mb-6 pt-4 border-t border-stone-100 flex items-center gap-2.5 flex-wrap">
                      <span className="text-[10px] font-extrabold tracking-[0.14em] text-stone-400 uppercase flex items-center gap-1.5 whitespace-nowrap">
                        <i className="ri-star-fill text-amber-400 text-xs" />
                        POPULAR:
                      </span>
                      {POPULAR_STRIP.map((item) => (
                        <button
                          key={item.label}
                          onClick={() => go(item.href)}
                          className="text-[12px] font-semibold text-stone-600 hover:text-amber-700 bg-stone-50 hover:bg-amber-50 border border-stone-100 hover:border-amber-200 px-3.5 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                        >
                          {item.label}
                        </button>
                      ))}
                      <button
                        onClick={() => go('/category/vertical-blind-parts')}
                        className="text-[12px] font-bold text-amber-700 hover:text-amber-800 ml-auto flex items-center gap-1 cursor-pointer whitespace-nowrap transition-colors"
                      >
                        View All Parts <i className="ri-arrow-right-line text-sm" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* SHOP BY CATEGORY */}
                <div
                  className="relative"
                  onMouseEnter={() => openMenu('category')}
                  onMouseLeave={scheduleClose}
                >
                  <button
                    className={`flex items-center gap-1 px-4 py-2 text-[13px] font-semibold rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                      activeMenu === 'category' ? 'text-amber-700 bg-amber-50' : 'text-stone-700 hover:text-stone-900 hover:bg-stone-50'
                    }`}
                  >
                    Shop by Category
                    <i className={`ri-arrow-down-s-line text-base transition-transform duration-150 ${activeMenu === 'category' ? 'rotate-180' : ''}`} />
                  </button>

                  <div
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                    className={`absolute top-[calc(100%+1px)] left-0 w-56 bg-white border border-stone-100 rounded-xl overflow-hidden transition-all duration-200 ${
                      activeMenu === 'category'
                        ? 'opacity-100 visible translate-y-0'
                        : 'opacity-0 invisible -translate-y-2 pointer-events-none'
                    }`}
                    style={{ boxShadow: '0 16px 40px rgba(0,0,0,0.10)', zIndex: 9999 }}
                  >
                    <div className="px-3 pt-3 pb-1">
                      <span className="text-[10px] font-extrabold tracking-[0.12em] text-stone-400 uppercase">Browse by Type</span>
                    </div>
                    {CATEGORY_LIST.map((item) => (
                      <button
                        key={item.label}
                        onClick={() => go(item.href)}
                        className="flex items-center gap-3 w-full px-3 py-2.5 text-[13px] text-stone-600 hover:text-stone-900 hover:bg-stone-50 transition-colors cursor-pointer text-left"
                      >
                        <span className="w-5 h-5 flex items-center justify-center text-stone-400 flex-shrink-0">
                          <i className={`${item.icon} text-sm`} />
                        </span>
                        {item.label}
                      </button>
                    ))}
                    <div className="p-3 pt-1">
                      <button
                        onClick={() => go('/category/horizontal-blind-parts')}
                        className="w-full text-xs font-bold text-amber-600 hover:text-amber-700 py-2 border border-amber-200 hover:border-amber-300 rounded-lg hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        View All Categories →
                      </button>
                    </div>
                  </div>
                </div>

                {/* POPULAR ITEMS */}
                <div
                  className="relative"
                  onMouseEnter={() => openMenu('popular')}
                  onMouseLeave={scheduleClose}
                >
                  <button
                    className={`flex items-center gap-1 px-4 py-2 text-[13px] font-semibold rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                      activeMenu === 'popular' ? 'text-amber-700 bg-amber-50' : 'text-stone-700 hover:text-stone-900 hover:bg-stone-50'
                    }`}
                  >
                    Popular Items
                    <i className={`ri-arrow-down-s-line text-base transition-transform duration-150 ${activeMenu === 'popular' ? 'rotate-180' : ''}`} />
                  </button>

                  <div
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                    className={`absolute top-[calc(100%+1px)] left-0 w-72 bg-white border border-stone-100 rounded-xl overflow-hidden transition-all duration-200 ${
                      activeMenu === 'popular'
                        ? 'opacity-100 visible translate-y-0'
                        : 'opacity-0 invisible -translate-y-2 pointer-events-none'
                    }`}
                    style={{ boxShadow: '0 16px 40px rgba(0,0,0,0.10)', zIndex: 9999 }}
                  >
                    <div className="px-4 pt-3.5 pb-2 border-b border-stone-50">
                      <span className="text-[10px] font-extrabold tracking-[0.12em] text-stone-400 uppercase">Best Sellers</span>
                    </div>
                    {POPULAR_ITEMS.map((item) => (
                      <button
                        key={item.label}
                        onClick={() => go(item.href)}
                        className="flex items-start gap-3 w-full px-4 py-3 hover:bg-amber-50 transition-colors cursor-pointer group text-left"
                      >
                        <span className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <i className="ri-star-fill text-amber-400 text-[11px]" />
                        </span>
                        <div>
                          <div className="text-sm font-semibold text-stone-800 group-hover:text-amber-700 whitespace-nowrap transition-colors">{item.label}</div>
                          <div className="text-[11px] text-stone-400 mt-0.5">{item.desc}</div>
                        </div>
                      </button>
                    ))}
                    <div className="px-4 pt-2 pb-3 border-t border-stone-50">
                      <button
                        onClick={() => go('/category/vertical-blind-parts')}
                        className="w-full text-xs font-bold text-amber-600 hover:text-amber-700 py-1.5 border border-amber-200 hover:border-amber-300 rounded-lg hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        View All Best Sellers →
                      </button>
                    </div>
                  </div>
                </div>

                {/* Flat nav links */}
                <button onClick={() => go('/about')} className="px-4 py-2 text-[13px] font-medium text-stone-600 hover:text-stone-900 hover:bg-stone-50 rounded-md transition-colors cursor-pointer whitespace-nowrap">
                  How To / Guides
                </button>
                <button onClick={() => go('/about')} className="px-4 py-2 text-[13px] font-medium text-stone-600 hover:text-stone-900 hover:bg-stone-50 rounded-md transition-colors cursor-pointer whitespace-nowrap">
                  About Us
                </button>
                <button onClick={() => go('/about')} className="px-4 py-2 text-[13px] font-medium text-stone-600 hover:text-stone-900 hover:bg-stone-50 rounded-md transition-colors cursor-pointer whitespace-nowrap">
                  Contact
                </button>
              </nav>

              {/* ── Right icons */}
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <button
                  onClick={() => { setActiveMenu(null); setSearchOpen(!searchOpen); }}
                  aria-label="Search"
                  className={`w-9 h-9 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                    searchOpen ? 'text-amber-700 bg-amber-50' : 'text-stone-500 hover:text-stone-900 hover:bg-stone-50'
                  }`}
                >
                  <i className="ri-search-line text-[17px]" />
                </button>
                <button aria-label="Account" className="w-9 h-9 flex items-center justify-center rounded-md text-stone-500 hover:text-stone-900 hover:bg-stone-50 transition-colors cursor-pointer">
                  <i className="ri-user-3-line text-[17px]" />
                </button>
                <button aria-label="Cart" className="relative w-9 h-9 flex items-center justify-center rounded-md text-stone-500 hover:text-stone-900 hover:bg-stone-50 transition-colors cursor-pointer">
                  <i className="ri-shopping-bag-3-line text-[17px]" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Expandable search bar */}
        <div className={`overflow-hidden transition-all duration-200 bg-stone-50 border-t border-stone-100 ${searchOpen ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}>
          <form onSubmit={handleSearch} className="max-w-screen-2xl mx-auto px-4 xl:px-10 py-3 flex gap-2">
            <div className="flex-1 flex rounded-lg overflow-hidden border border-stone-200 focus-within:border-amber-400 transition-colors bg-white">
              <input
                ref={searchRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search blind parts by name, SKU, or description…"
                className="flex-1 px-4 py-2.5 text-sm text-stone-800 placeholder-stone-400 outline-none bg-white"
              />
              <button type="submit" className="bg-amber-600 hover:bg-amber-500 text-white px-5 py-2.5 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5">
                <i className="ri-search-line text-sm" />
                Search
              </button>
            </div>
            <button
              type="button"
              onClick={() => setSearchOpen(false)}
              className="w-10 h-10 flex items-center justify-center text-stone-400 hover:text-stone-700 cursor-pointer transition-colors flex-shrink-0 rounded-md hover:bg-stone-100"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </form>
        </div>
      </div>

      {/* ══════════════════ MOBILE NAV ══════════════════ */}
      <div className="lg:hidden border-b border-stone-100">
        <div className="max-w-screen-2xl mx-auto px-4">
          <div className="flex items-center h-14 gap-4">
            <button onClick={() => go('/')} className="flex items-center gap-2 cursor-pointer flex-shrink-0 group">
              <div className="w-8 h-8 flex items-center justify-center bg-amber-600 rounded-md group-hover:bg-amber-500 transition-colors flex-shrink-0">
                <i className="ri-home-gear-line text-white text-sm" />
              </div>
              <span className="text-stone-900 font-extrabold text-sm tracking-widest whitespace-nowrap">BLINDS USA INC</span>
            </button>
            <div className="flex items-center gap-1 ml-auto">
              <button onClick={() => setSearchOpen(!searchOpen)} aria-label="Search" className="w-9 h-9 flex items-center justify-center rounded-md text-stone-500 hover:text-stone-900 hover:bg-stone-50 transition-colors cursor-pointer">
                <i className="ri-search-line text-lg" />
              </button>
              <button aria-label="Cart" className="w-9 h-9 flex items-center justify-center rounded-md text-stone-500 hover:text-stone-900 hover:bg-stone-50 transition-colors cursor-pointer">
                <i className="ri-shopping-bag-3-line text-lg" />
              </button>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="Menu"
                className="w-9 h-9 flex items-center justify-center rounded-md text-stone-600 hover:text-stone-900 hover:bg-stone-50 transition-colors cursor-pointer"
              >
                <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile search */}
        <div className={`overflow-hidden transition-all duration-200 bg-stone-50 border-t border-stone-100 ${searchOpen ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}>
          <form onSubmit={handleSearch} className="px-4 py-2.5 flex gap-2">
            <div className="flex-1 flex rounded-lg overflow-hidden border border-stone-200 focus-within:border-amber-400 transition-colors bg-white">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search parts…"
                className="flex-1 px-3 py-2 text-sm text-stone-800 placeholder-stone-400 outline-none bg-white"
              />
              <button type="submit" className="bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap">
                Go
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ══════════════════ MOBILE DRAWER ══════════════════ */}
      <div className={`lg:hidden bg-white border-b border-stone-100 overflow-y-auto transition-all duration-300 ${mobileOpen ? 'max-h-[82vh] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}>
        <div className="px-4 py-3 space-y-0.5">

          {/* Shop Parts */}
          <MobileSection label="Shop Parts" icon="ri-store-2-line" isOpen={mobileExpanded === 'parts'} onToggle={() => toggleMobileSection('parts')}>
            {MEGA_COLUMNS.map((col) => (
              <div key={col.heading} className="mb-4 last:mb-0">
                <p className="text-[10px] font-extrabold tracking-[0.14em] text-stone-400 uppercase px-2 mb-1.5">{col.heading}</p>
                {col.links.map((link) => (
                  <button key={link.label} onClick={() => go(link.href)} className="flex items-center gap-2 w-full px-2 py-2 text-sm text-stone-700 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer text-left">
                    <i className="ri-arrow-right-s-line text-stone-300 text-base flex-shrink-0" />
                    {link.label}
                  </button>
                ))}
              </div>
            ))}
          </MobileSection>

          {/* Shop by Category */}
          <MobileSection label="Shop by Category" icon="ri-apps-2-line" isOpen={mobileExpanded === 'category'} onToggle={() => toggleMobileSection('category')}>
            {CATEGORY_LIST.map((item) => (
              <button key={item.label} onClick={() => go(item.href)} className="flex items-center gap-3 w-full px-2 py-2.5 text-sm text-stone-700 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer text-left">
                <span className="w-5 h-5 flex items-center justify-center text-stone-400 flex-shrink-0">
                  <i className={`${item.icon} text-sm`} />
                </span>
                {item.label}
              </button>
            ))}
          </MobileSection>

          {/* Popular Items */}
          <MobileSection label="Popular Items" icon="ri-star-line" isOpen={mobileExpanded === 'popular'} onToggle={() => toggleMobileSection('popular')}>
            {POPULAR_ITEMS.map((item) => (
              <button key={item.label} onClick={() => go(item.href)} className="flex items-start gap-3 w-full px-2 py-2.5 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer text-left">
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-star-fill text-amber-400 text-[11px]" />
                </span>
                <div>
                  <div className="text-sm font-semibold text-stone-800">{item.label}</div>
                  <div className="text-xs text-stone-400 mt-0.5">{item.desc}</div>
                </div>
              </button>
            ))}
          </MobileSection>

          {/* Flat links */}
          <div className="border-t border-stone-50 pt-2 mt-2 space-y-0.5">
            {[
              { label: 'How To / Guides', icon: 'ri-book-open-line', href: '/about' },
              { label: 'About Us', icon: 'ri-information-line', href: '/about' },
              { label: 'Contact', icon: 'ri-phone-line', href: '/about' },
            ].map((item) => (
              <button key={item.label} onClick={() => go(item.href)} className="flex items-center gap-3 w-full px-2 py-3 text-sm text-stone-600 hover:text-stone-900 hover:bg-stone-50 rounded-lg transition-colors cursor-pointer text-left">
                <span className="w-5 h-5 flex items-center justify-center text-stone-400 flex-shrink-0">
                  <i className={`${item.icon} text-sm`} />
                </span>
                {item.label}
              </button>
            ))}
          </div>

          {/* CTA */}
          <button
            onClick={() => go('/category/vertical-blind-parts')}
            className="w-full bg-amber-600 hover:bg-amber-500 text-white text-sm font-extrabold px-5 py-3.5 rounded-lg tracking-wide whitespace-nowrap mt-3 mb-2 cursor-pointer flex items-center justify-center gap-2 transition-colors"
          >
            <i className="ri-store-2-line" />
            Shop All Parts
          </button>
        </div>
      </div>
    </header>
  );
}

/* ─────────────────────────────────────────────────────────────
   MOBILE SECTION COMPONENT
───────────────────────────────────────────────────────────── */
interface MobileSectionProps {
  label: string;
  icon: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function MobileSection({ label, icon, isOpen, onToggle, children }: MobileSectionProps) {
  return (
    <div className="border-b border-stone-50 last:border-0">
      <button onClick={onToggle} className="flex items-center justify-between w-full px-2 py-3.5 text-[13px] font-semibold text-stone-800 cursor-pointer">
        <span className="flex items-center gap-3">
          <span className="w-5 h-5 flex items-center justify-center text-amber-600 flex-shrink-0">
            <i className={`${icon} text-sm`} />
          </span>
          {label}
        </span>
        <i className={`ri-arrow-down-s-line text-stone-400 text-base transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-250 ${isOpen ? 'max-h-[800px] pb-3' : 'max-h-0'}`}>
        <div className="pl-2">{children}</div>
      </div>
    </div>
  );
}
